import type { LucideIcon } from "lucide-react"
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from "lucide-react"
import { useState } from "react"
import { NavLink, Outlet } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { sidebarActiveClass, type TopSection } from "@/lib/nav"
import { cn } from "@/lib/utils"

export type SectionNavItem = {
  icon: LucideIcon
  label: string
  to: string
  end?: boolean
}

const COLLAPSED_KEY = "ho-kitchen-sidebar-collapsed"

export function SectionSidebarLayout({
  navId,
  navItems,
}: {
  navId: TopSection
  navItems: SectionNavItem[]
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1"
    } catch {
      return false
    }
  })

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0")
      } catch {
        /* ignore */
      }
      return next
    })
  }

  return (
    <div className="flex size-full min-h-0 flex-1 overflow-hidden">
      <aside
        className={cn(
          "relative flex min-h-0 shrink-0 flex-col self-stretch border-r border-border bg-sidebar/50 transition-[width] duration-200",
          collapsed ? "w-12" : "w-56",
        )}
      >
        <Button
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn("absolute top-1.5 z-10 size-7", collapsed ? "right-2.5" : "right-1.5")}
          onClick={toggle}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
        <TooltipProvider>
          <nav
            aria-label="Section"
            className={cn(
              "min-h-0 flex-1 space-y-1 overflow-y-auto pb-3",
              collapsed ? "px-1 pt-12" : "px-2.5 pt-10",
            )}
          >
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li className={cn(collapsed && "flex justify-center")} key={item.to}>
                  <SectionLink collapsed={collapsed} item={item} section={navId} />
                </li>
              ))}
            </ul>
          </nav>
        </TooltipProvider>
      </aside>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function SectionLink({
  collapsed,
  item,
  section,
}: {
  collapsed: boolean
  item: SectionNavItem
  section: TopSection
}) {
  const Icon = item.icon
  const link = (
    <NavLink
      aria-label={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-md text-sm transition-colors",
          collapsed ? "size-8 shrink-0 justify-center" : "w-full gap-2.5 px-3 py-2.5",
          isActive
            ? sidebarActiveClass(section)
            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        )
      }
      end={item.end}
      to={item.to}
    >
      <Icon className="size-4 shrink-0" />
      {collapsed ? null : <span>{item.label}</span>}
    </NavLink>
  )
  if (!collapsed) return link
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  )
}
